import { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { MobileProvider } from "./contexts/MobileContext";
import { applyGlassIntensity } from "./lib/glassIntensity";
import { installAutoGrowTextareas } from "./lib/autoGrowTextareas";
import AuthCallback from "./auth/AuthCallback";
import LandingPage from "./pages/LandingPage";
import "./App.css";
import { Toaster } from "./components/ui/sonner";
import communityBgImg from "./assets/community-instruments-bg.webp";

// Lazy-loaded routes for code splitting
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const InstructorDirectory = lazy(() => import("./pages/InstructorDirectory"));
const InstructorProfile = lazy(() => import("./pages/InstructorProfile"));
const StudentPortal = lazy(() => import("./portals/student/StudentPortal"));
const InstructorPortal = lazy(() => import("./portals/instructor/InstructorPortal"));
const PaymentLogPage = lazy(() => import("./portals/instructor/PaymentLogPage"));
const CommunityPortal = lazy(() => import("./portals/community/CommunityPortal"));
const LiveStreamPage = lazy(() => import("./portals/community/LiveStreamPage"));
const AIAssistantFullPage = lazy(() => import("./pages/AIAssistantFullPage"));
const AdminPortal = lazy(() => import("./portals/admin/AdminPortal"));
const VirtualClassroom = lazy(() => import("./portals/classroom/VirtualClassroom"));
const WorkshopRoom = lazy(() => import("./portals/classroom/WorkshopRoom"));
const SharedChatView = lazy(() => import("./pages/SharedChatView"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const FAQPage = lazy(() => import("./pages/legal/FAQPage"));
const TermsPage = lazy(() => import("./pages/legal/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/legal/PrivacyPage"));
const AboutPage = lazy(() => import("./pages/legal/AboutPage"));
const SuccessStoriesPage = lazy(() => import("./pages/legal/SuccessStoriesPage"));
const BlogPage = lazy(() => import("./pages/legal/BlogPage"));
const ContactPage = lazy(() => import("./pages/legal/ContactPage"));

// MMS AI Textbook Studio — standalone product
/* eslint-disable */
import { lazy as _lazy } from "react";
const StudioWorkspaceModule = _lazy(() => import("./studio/StudioWorkspace.jsx"));
function StudioWorkspaceWrap({ section }) {
  return <StudioWorkspaceModule section={section} />;
}

const StudioLanding = lazy(() => import("./studio/StudioLanding"));
const StudioPricing = lazy(() => import("./studio/StudioPricing"));
const StudioAuth = lazy(() => import("./studio/StudioAuth"));
const StudioResetPassword = lazy(() => import("./studio/StudioResetPassword"));
const StudioDashboard = lazy(() => import("./studio/StudioDashboard"));
const StudioProfile = lazy(() => import("./studio/StudioProfile"));
const StudioProviders = lazy(() => import("./studio/StudioProviders"));

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-10 h-10 rounded-full border-2 border-foreground/20 border-t-foreground animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) {
    const map = { admin: '/admin', instructor: '/instructor', student: '/student', community: '/community' };
    return <Navigate to={map[user.role] || '/'} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment synchronously — prevents race conditions with AuthCallback
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/courses" element={<CoursesPage />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/community/verify" element={<VerifyEmailPage />} />
        <Route path="/live/:streamId" element={<LiveStreamPage />} />
        <Route path="/ai" element={<ProtectedRoute><AIAssistantFullPage /></ProtectedRoute>} />
        <Route path="/instructors" element={<InstructorDirectory />} />
        <Route path="/instructors/:id" element={<InstructorProfile />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/success-stories" element={<SuccessStoriesPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/booking" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
        <Route path="/instructor/payment-log" element={<ProtectedRoute roles={['instructor','admin']}><PaymentLogPage /></ProtectedRoute>} />
        <Route path="/student/*" element={<ProtectedRoute roles={['student','admin']}><StudentPortal /></ProtectedRoute>} />
        <Route path="/instructor/*" element={<ProtectedRoute roles={['instructor','admin']}><InstructorPortal /></ProtectedRoute>} />
        <Route path="/community/*" element={<ProtectedRoute><CommunityPortal /></ProtectedRoute>} />
        <Route path="/admin/*" element={<AdminPortal />} />
        <Route path="/classroom/:roomId" element={<ProtectedRoute><VirtualClassroom /></ProtectedRoute>} />
        <Route path="/classroom" element={<ProtectedRoute><VirtualClassroom /></ProtectedRoute>} />
        <Route path="/workshop/:workshopId" element={<WorkshopRoom />} />
        <Route path="/shared/chat/:shareId" element={<SharedChatView />} />

        {/* ═══════ MMS AI Textbook Studio (standalone product) ═══════ */}
        <Route path="/studio" element={<StudioProviders><StudioLanding /></StudioProviders>} />
        <Route path="/studio/pricing" element={<StudioProviders><StudioPricing /></StudioProviders>} />
        <Route path="/studio/signin" element={<StudioProviders><StudioAuth mode="signin" /></StudioProviders>} />
        <Route path="/studio/signup" element={<StudioProviders><StudioAuth mode="signup" /></StudioProviders>} />
        <Route path="/studio/reset-password" element={<StudioProviders><StudioResetPassword /></StudioProviders>} />
        <Route path="/studio/instructor" element={
          <StudioProviders>
            <Suspense fallback={null}><StudioWorkspaceWrap section="instructor"/></Suspense>
          </StudioProviders>
        } />
        <Route path="/studio/student" element={
          <StudioProviders>
            <Suspense fallback={null}><StudioWorkspaceWrap section="student"/></Suspense>
          </StudioProviders>
        } />
        <Route path="/studio/profile" element={
          <StudioProviders>
            <Suspense fallback={null}><StudioProfile /></Suspense>
          </StudioProviders>
        } />
        <Route path="/studio/app" element={<Navigate to="/studio/instructor" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  useEffect(() => { import('./portals/community/i18n').then(m => m.applyLangFromStorage()); }, []);
  useEffect(() => {
    // Activate global auto-grow for every <textarea> in the app — the
    // helper attaches a MutationObserver to <body> so new textareas
    // (modals, route changes, lazy components) are upgraded automatically.
    installAutoGrowTextareas();
  }, []);
  useEffect(() => {
    // Inject the shared instrument background URL as a CSS var so themed-page-bg
    // and portal-bg can reference it without webpack trying to resolve a literal
    // `/community-instruments-bg.jpg` path inside the CSS file.
    document.documentElement.style.setProperty('--mms-shell-img', `url(${communityBgImg})`);
  }, []);
  useEffect(() => {
    // Hydrate Glass Intensity globally so the slider works on every portal.
    try {
      const raw = localStorage.getItem('mms_settings_glass_intensity');
      const pct = raw !== null ? JSON.parse(raw) : 80;
      applyGlassIntensity(pct);
    } catch { /* noop */ }
  }, []);
  return (
    <ThemeProvider attribute={["class", "data-theme"]} defaultTheme="dark" enableSystem={false}>
      <MobileProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster richColors position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </MobileProvider>
    </ThemeProvider>
  );
}
